// src/components/dashboard/SelectedPersonPanel.jsx
import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import EnrollStudentModal from "../enrollments/EnrollStudentModal";
import * as EnrollmentAPI from "../../util/enrollment_api_util";
import * as ApprenticeshipAPI from "../../util/apprenticeship_api_util";
import "./selectedpersonpanel.css"
import PostCourseStaffOverlayLauncher from "./PostCourseStaffOverlayLauncher.js";
import PostCourseStaffReviewPanel from "./PostCourseStaffReviewPanel.js";


import { createPortal } from "react-dom";


const isApprenticeshipCertificationEarned = (appr) => {
  const csArr = Array.isArray(appr?.caseStudies) ? appr.caseStudies : [];
  if (!appr?.unlocked) return false;

  // ✅ must have all 3 case studies completed
  if (csArr.length !== 3) return false;

  // ✅ every first + second session must be approved
  return csArr.every((cs) => {
    const first = String(cs?.firstSession?.status || "").toLowerCase();
    const second = String(cs?.secondSession?.status || "").toLowerCase();
    return first === "approved" && second === "approved";
  });
};

const getPersonIdSafe = (p) => String(p?._id || p?.id || "");

// ---------- local helpers ----------
const formatDateUTC = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const formatDateLocal = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US");
};

const formatDateTimeLocal = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleString("en-US");
};

const isEnrollmentExpired = (enr) => {
  if (!enr) return false;
  if (String(enr?.status || "").toLowerCase() === "expired") return true;
  const expiry = enr?.expiryDate ? new Date(enr.expiryDate) : null;
  return !!expiry && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
};

const normalizeEnrollmentsResponse = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.enrollments)) return res.enrollments;
  if (Array.isArray(res?.data?.enrollments)) return res.data.enrollments;
  return [];
};

const toLocalSessionDraft = (value) => {
  if (!value) return { sessionDate: "", sessionTime: "" };
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return { sessionDate: "", sessionTime: "" };
  return {
    sessionDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    sessionTime: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

const buildSessionAtValue = (draft) => {
  const sessionDate = String(draft?.sessionDate || "").trim();
  const sessionTime = String(draft?.sessionTime || "").trim();
  if (!sessionDate) return "";
  return `${sessionDate}T${sessionTime || "00:00"}`;
};

const formatDateInputLabel = (value) => {
  if (!value) return "mm / dd / yyyy";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return "mm / dd / yyyy";
  return `${month} / ${day} / ${year}`;
};

const monthStartFromValue = (value) => {
  const [year, month] = String(value || "").split("-").map(Number);
  if (year && month) return new Date(year, month - 1, 1);
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const formatTimeInputLabel = (value, options) => {
  if (!value) return "Choose time";
  return options.find((opt) => opt.value === value)?.label || "Choose time";
};

const safeStatusLabel = (s) => {
  const v = String(s || "").toLowerCase();
  if (!v) return "Unknown";
  if (v === "needed") return "Needed";
  if (v === "submitted") return "Submitted";
  if (v === "approved") return "Approved";
  if (v === "changes_requested") return "Changes requested";
  return s;
};

const statusClassFor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "submitted") return "dash-status-pill dash-status-submitted";
  if (s === "approved") return "dash-status-pill dash-status-approved";
  if (s === "changes_requested") return "dash-status-pill dash-status-changes";
  return "dash-status-pill dash-status-needed";
};

// ✅ Keep as a helper FUNCTION (do not shadow this name with a useMemo variable)
const hasPendingApprenticeshipReview = (appr) => {
  const csArr = Array.isArray(appr?.caseStudies) ? appr.caseStudies : [];
  return csArr.some((cs) => {
    const s1 = String(cs?.firstSession?.status || "").toLowerCase();
    const s2 = String(cs?.secondSession?.status || "").toLowerCase();
    return s1 === "pending_review" || s2 === "pending_review";
  });
};

const getCourseKeyFromEnrollment = (enr) => {
  const c = enr?.course || {};
  return c.key || c.courseKey || enr?.courseKey || enr?.key || "";
};

const getGlobalBloodborneFromUser = (user) => {
  const bb = user?.bloodborneCert || null;

  if (!bb) {
    return { required: true, status: "needed", url: "", submittedAt: null };
  }

  const url = bb.url || "";
  const status = bb.status || (url ? "submitted" : "needed");
  const submittedAtRaw = bb.submittedAt || bb.updatedAt || null;

  return {
    required: true,
    status,
    url,
    submittedAt: submittedAtRaw ? new Date(submittedAtRaw) : null,
    fileName: bb.fileName || "",
  };
};

const computeQuizAttemptStats = (enr) => {
  const progress = Array.isArray(enr?.progress) ? enr.progress : [];

  let totalAttempts = 0;
  let failedAttempts = 0;
  let passedSections = 0;

  let lastAttempt = null;

  const sections = progress
    .map((p) => {
      const attempts = Array.isArray(p?.attempts) ? p.attempts : [];
      const sectionObj = p?.section;
      const sectionId = typeof sectionObj === "string" ? sectionObj : String(sectionObj?._id || "");
      const sectionTitle = typeof sectionObj === "object" && sectionObj?.title ? sectionObj.title : "";

      totalAttempts += attempts.length;
      failedAttempts += attempts.filter((a) => !a?.isPerfect).length;
      if (p?.quizPassed) passedSections += 1;

      const latest = attempts.length ? attempts[attempts.length - 1] : null;

      if (latest?.submittedAt) {
        const t = new Date(latest.submittedAt).getTime();
        const cur = lastAttempt?.submittedAt ? new Date(lastAttempt.submittedAt).getTime() : 0;
        if (!cur || t > cur) {
          lastAttempt = { ...latest, sectionId };
        }
      }

      return {
        sectionId,
        sectionTitle,
        quizPassed: !!p?.quizPassed,
        attempts,
        lastAttempt: latest
          ? {
              submittedAt: latest.submittedAt,
              correctCount: latest.correctCount,
              total: latest.total,
              isPerfect: !!latest.isPerfect,
            }
          : null,
        failures: attempts.filter((a) => !a?.isPerfect).length,
      };
    })
    .sort((a, b) => {
      const aT = a.lastAttempt?.submittedAt ? new Date(a.lastAttempt.submittedAt).getTime() : 0;
      const bT = b.lastAttempt?.submittedAt ? new Date(b.lastAttempt.submittedAt).getTime() : 0;
      return bT - aT;
    });

  return { totalAttempts, failedAttempts, passedSections, lastAttempt, sections };
};

const prettyApprStatus = (s) => {
  const v = String(s || "").toLowerCase();
  if (v === "not_submitted") return "Not submitted";
  if (v === "pending_review") return "Pending review";
  if (v === "approved") return "Approved";
  if (v === "changes_requested") return "Changes requested";
  return s || "Unknown";
};

const apprStatusClass = (s) => {
  const v = String(s || "").toLowerCase();
  if (v === "pending_review") return "dash-status-pill dash-status-submitted";
  if (v === "approved") return "dash-status-pill dash-status-approved";
  if (v === "changes_requested") return "dash-status-pill dash-status-changes";
  return "dash-status-pill dash-status-needed";
};

const groupPhotos = (photos = []) => {
  const before = [];
  const after = [];
  (photos || []).forEach((p) => {
    if (p?.kind === "before") before.push(p);
    else if (p?.kind === "after") after.push(p);
  });
  return { before, after };
};



// ---------- component ----------
const SelectedPersonPanel = ({
  selectedPerson,
  onClose,

  isPendingStudent,
  isPendingMaster,

  gmCourses = [],
  resolveCourseFromKey,
  MODEL_TIME_SLOTS_BY_COURSE_KEY,
  requiresModelForCourseKey,
  validateScheduleForKeys,

  getPersonId,
  getScheduleMapForPerson,
  refreshSelectedPersonRef,
  scheduleByPersonId,
  setScheduleByPersonId,

  queuedCoursesByPersonId,
  setQueuedCoursesByPersonId,
  pendingRemovalsByPersonId,
  setPendingRemovalsByPersonId,

  getEnrollmentsForSelectedPerson,
  setPersonEnrollmentsById,

  approveStudent,
  approveMaster,
  onAssignUserToCourse,
  onRemoveUserFromCourse,

  fetchPendingStudents,
  fetchActiveStudents,
  fetchPendingMasters,
  fetchMasters,
  fetchActiveMasters,

  fetchEnrollmentsForUser,

  isEnrollModalOpen,
  setIsEnrollModalOpen,
  modalCourseKey,
  setModalCourseKey,

  showCourseConfirm,
  setShowCourseConfirm,
  courseConfirmText,
  setCourseConfirmText,
  pendingCourseCommit,
  setPendingCourseCommit,
  isCourseSaving,
  setIsCourseSaving,
  currentUser,
  setSelectedPerson,
  POST_COURSE_MODULES_BY_KEY,
  socket
  
}) => {
  const panelRef = useRef(null);

    

    
  // ✅ derive safely BEFORE any early return (hooks must always run in same order)
  const selectedRole = String(selectedPerson?.role || "").toLowerCase();
  const isSelectedStudent = selectedRole === "student";
  const isSelectedMaster = selectedRole === "master";
  const isPending = !!isPendingStudent || !!isPendingMaster;
    const [isEnrollModalOpenLocal, setIsEnrollModalOpenLocal] = useState(false);
    const [modalCourseKeyLocal, setModalCourseKeyLocal] = useState(null);


    const effectiveIsEnrollModalOpen =
  typeof isEnrollModalOpen === "boolean" ? isEnrollModalOpen : isEnrollModalOpenLocal;

const effectiveSetIsEnrollModalOpen =
  typeof setIsEnrollModalOpen === "function" ? setIsEnrollModalOpen : setIsEnrollModalOpenLocal;

const effectiveModalCourseKey =
  modalCourseKey !== undefined ? modalCourseKey : modalCourseKeyLocal;

const effectiveSetModalCourseKey =
  typeof setModalCourseKey === "function" ? setModalCourseKey : setModalCourseKeyLocal;

  
  // ✅ local fallbacks (in case parent doesn't pass these props)
const [showCourseConfirmLocal, setShowCourseConfirmLocal] = useState(false);
const [courseConfirmTextLocal, setCourseConfirmTextLocal] = useState("");
const [pendingCourseCommitLocal, setPendingCourseCommitLocal] = useState(null);

// ✅ effective versions (prefer props if provided)
const effectiveShowCourseConfirm =
  typeof showCourseConfirm === "boolean" ? showCourseConfirm : showCourseConfirmLocal;

const effectiveSetShowCourseConfirm =
  typeof setShowCourseConfirm === "function" ? setShowCourseConfirm : setShowCourseConfirmLocal;

const effectiveCourseConfirmText =
  typeof courseConfirmText === "string" ? courseConfirmText : courseConfirmTextLocal;

const effectiveSetCourseConfirmText =
  typeof setCourseConfirmText === "function" ? setCourseConfirmText : setCourseConfirmTextLocal;

const effectivePendingCourseCommit =
  typeof pendingCourseCommit === "function" ? pendingCourseCommit : pendingCourseCommitLocal;

const effectiveSetPendingCourseCommit =
  typeof setPendingCourseCommit === "function" ? setPendingCourseCommit : setPendingCourseCommitLocal;


    // ===== INK Lightbox (for apprenticeship submission photos) =====
  const [apprLightbox, setApprLightbox] = useState({
    isOpen: false,
    items: [],
    index: 0,
    title: "",
  });

  const openApprLightbox = useCallback((items, index = 0, title = "") => {
    const arr = Array.isArray(items) ? items.filter((x) => x?.url) : [];
    if (!arr.length) return;

    setApprLightbox({
      isOpen: true,
      items: arr,
      index: Math.max(0, Math.min(arr.length - 1, Number(index || 0))),
      title: title || "",
    });
  }, []);

  const closeApprLightbox = useCallback(() => {
    setApprLightbox((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const apprPrev = useCallback(() => {
    setApprLightbox((prev) => {
      const n = prev.items.length;
      if (!n) return prev;
      return { ...prev, index: (prev.index - 1 + n) % n };
    });
  }, []);

  const apprNext = useCallback(() => {
    setApprLightbox((prev) => {
      const n = prev.items.length;
      if (!n) return prev;
      return { ...prev, index: (prev.index + 1) % n };
    });
  }, []);

  useEffect(() => {
    if (!apprLightbox.isOpen) return;

    // lock body scroll (you already use modal-open elsewhere; reuse it)
    document.body.classList.add("modal-open");

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeApprLightbox();
      if (e.key === "ArrowLeft") apprPrev();
      if (e.key === "ArrowRight") apprNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [apprLightbox.isOpen, closeApprLightbox, apprPrev, apprNext]);


  // ✅ pid MUST be defined before any hook uses it
  const pid = useMemo(() => {
    if (!selectedPerson) return "";
    return typeof getPersonId === "function" ? getPersonId(selectedPerson) : getPersonIdSafe(selectedPerson);
  }, [selectedPerson, getPersonId]);

  const enrollmentsForPerson =
    selectedPerson && isSelectedStudent && !isPending ? getEnrollmentsForSelectedPerson(selectedPerson) : [];

  const sortedEnrollments = useMemo(() => {
    const arr = [...(enrollmentsForPerson || [])];
    arr.sort((a, b) => {
      const ta = a?.startDate ? new Date(a.startDate).getTime() : Number.POSITIVE_INFINITY;
      const tb = b?.startDate ? new Date(b.startDate).getTime() : Number.POSITIVE_INFINITY;
      return ta - tb;
    });
    return arr;
  }, [enrollmentsForPerson]);

  const optedInPhotoPackageEnrollments = useMemo(() => {
    return sortedEnrollments.filter((enr) => {
      const selection = String(enr?.photoPackage?.selection || "").toLowerCase();
      return selection === "opt_in";
    });
  }, [sortedEnrollments]);

  const optedInPhotoPackageCourseSummary = useMemo(() => {
    if (!optedInPhotoPackageEnrollments.length) return "";
    return optedInPhotoPackageEnrollments
      .map((enr) => enr?.course?.title || getCourseKeyFromEnrollment(enr) || "Course")
      .filter(Boolean)
      .join(" · ");
  }, [optedInPhotoPackageEnrollments]);

  const [apprenticeship, setApprenticeship] = useState(null);
  const certificationEarned = isApprenticeshipCertificationEarned(apprenticeship);

  const [apprenticeshipLoading, setApprenticeshipLoading] = useState(false);
  const [apprenticeshipErr, setApprenticeshipErr] = useState("");
  const [apprenticeshipSavingKey, setApprenticeshipSavingKey] = useState("");
  const [apprenticeshipBookingLink, setApprenticeshipBookingLink] = useState("");
  const [photoPackageDrafts, setPhotoPackageDrafts] = useState({});
  const [photoPackagePaidDrafts, setPhotoPackagePaidDrafts] = useState({});
  const [photoPackageSavingKey, setPhotoPackageSavingKey] = useState("");
  const [apprenticeshipBookingLinkSaved, setApprenticeshipBookingLinkSaved] = useState("");
  const [apprenticeshipSessionDrafts, setApprenticeshipSessionDrafts] = useState({});
  const [apprenticeshipSessionSavingKey, setApprenticeshipSessionSavingKey] = useState("");
  const [apprenticeshipSessionEditing, setApprenticeshipSessionEditing] = useState({});
  const [openSessionDatePickerKey, setOpenSessionDatePickerKey] = useState("");
  const [sessionDatePickerMonthByKey, setSessionDatePickerMonthByKey] = useState({});
  const [openSessionTimePickerKey, setOpenSessionTimePickerKey] = useState("");

  const viewerRole = String(currentUser?.role || "").toLowerCase();
  const isStaffViewer = viewerRole === "trainer" || viewerRole === "master" || viewerRole === "grandmaster";

  // --- collapsible sections ---
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [apprenticeshipOpen, setApprenticeshipOpen] = useState(false);
  const [apprenticeshipAdminOpen, setApprenticeshipAdminOpen] = useState(false);
  const [photoPackageAdminOpen, setPhotoPackageAdminOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // ✅ Memo boolean (do NOT name this the same as the helper function)
  const hasPendingApprReviewMemo = useMemo(() => {
    const cs = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies : [];
    return cs.some((c) => {
      const a = String(c?.firstSession?.status || "").toLowerCase() === "pending_review";
      const b = String(c?.secondSession?.status || "").toLowerCase() === "pending_review";
      return a || b;
    });
  }, [apprenticeship]);

  // So we don't "fight" the user (auto-open only once unless user toggles)
  const [apprUserToggled, setApprUserToggled] = useState(false);

  const apprenticeshipHasPending = hasPendingApprenticeshipReview(apprenticeship);

  useEffect(() => {
    if (!apprenticeship) return;
    const saved = String(apprenticeship.bookingLink || "");
    setApprenticeshipBookingLink(saved);
    setApprenticeshipBookingLinkSaved(saved);
  }, [apprenticeship?.bookingLink]);

  useEffect(() => {
    setApprenticeshipAdminOpen(false);
    setPhotoPackageAdminOpen(false);
    setEmailCopied(false);
  }, [pid]);

  const handleCopyEmail = useCallback(async () => {
    const rawEmail = String(selectedPerson?.email || "").trim();
    if (!rawEmail) return;
    try {
      await navigator.clipboard.writeText(rawEmail);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1800);
    } catch (err) {
      console.error("Could not copy email:", err);
    }
  }, [selectedPerson]);

  const refreshSelectedStudentEnrollments = useCallback(async () => {
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId || !fetchEnrollmentsForUser) return [];

    const res = await fetchEnrollmentsForUser(studentId);
    const enrollmentsArr = normalizeEnrollmentsResponse(res);
    setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(studentId)]: enrollmentsArr }));
    return enrollmentsArr;
  }, [fetchEnrollmentsForUser, selectedPerson, setPersonEnrollmentsById]);

  useEffect(() => {
    const next = {};
    const paidNext = {};
    optedInPhotoPackageEnrollments.forEach((enr) => {
      next[String(enr?._id || "")] = String(enr?.photoPackage?.deliveryLink || "");
      paidNext[String(enr?._id || "")] = !!enr?.photoPackage?.paid;
    });
    setPhotoPackageDrafts(next);
    setPhotoPackagePaidDrafts(paidNext);
  }, [optedInPhotoPackageEnrollments]);

  useEffect(() => {
    const csArr = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies : [];
    const next = {};
    const editNext = {};
    csArr.forEach((cs, idx) => {
      ["firstSession", "secondSession"].forEach((key) => {
        const sess = cs?.[key];
        const { sessionDate, sessionTime } = toLocalSessionDraft(sess?.sessionAt);
        next[`${idx}:${key}`] = {
          clientName: String(sess?.clientName || ""),
          sessionDate,
          sessionTime,
        };
        editNext[`${idx}:${key}`] = false;
      });
    });
    setApprenticeshipSessionDrafts(next);
    setApprenticeshipSessionEditing(editNext);
    setOpenSessionDatePickerKey("");
    setSessionDatePickerMonthByKey({});
    setOpenSessionTimePickerKey("");
  }, [apprenticeship]);

  // --- iOS-safe body scroll lock ---
const lockBodyScroll = useCallback(() => {
  const body = document.body;
  if (body.classList.contains("modal-open")) return;

  const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
  body.dataset.scrollY = String(scrollY);

  body.classList.add("modal-open");
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
}, []);

const unlockBodyScroll = useCallback(() => {
  const body = document.body;
  if (!body.classList.contains("modal-open")) return;

  const scrollY = parseInt(body.dataset.scrollY || "0", 10) || 0;

  body.classList.remove("modal-open");
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.right = "";
  body.style.width = "";
  body.dataset.scrollY = "";

  window.scrollTo(0, scrollY);
}, []);

const anyModalOpen = !!effectiveIsEnrollModalOpen || !!effectiveShowCourseConfirm;




const getScrollContainer = () => {
  // pick the real scroller in your app (adjust if needed)
  return (
    document.querySelector(".dash-shell") ||
    document.querySelector("#root") ||
    document.scrollingElement ||
    document.documentElement
  );
};

const lockAppScroll = useCallback(() => {
  const scroller = getScrollContainer();
  if (!scroller) return;

  // prevent repeated lock
  if (scroller.dataset.locked === "1") return;

  const scrollTop = scroller.scrollTop || window.scrollY || 0;
  scroller.dataset.locked = "1";
  scroller.dataset.scrollTop = String(scrollTop);

  // Freeze whichever element actually scrolls
  scroller.style.overflow = "hidden";

  // Also block iOS touch scroll behind overlays
  const prevent = (e) => e.preventDefault();
  window.__COSMETINK_SCROLL_PREVENT__ = prevent;
  window.addEventListener("touchmove", prevent, { passive: false });
}, []);

const unlockAppScroll = useCallback(() => {
  const scroller = getScrollContainer();
  if (!scroller) return;

  if (scroller.dataset.locked !== "1") return;

  scroller.dataset.locked = "0";
  scroller.style.overflow = "";

  if (window.__COSMETINK_SCROLL_PREVENT__) {
    window.removeEventListener("touchmove", window.__COSMETINK_SCROLL_PREVENT__);
    window.__COSMETINK_SCROLL_PREVENT__ = null;
  }
}, []);

const getDashShell = () => document.querySelector(".dash-shell");

const lockDashShellScroll = useCallback(() => {
  const shell = getDashShell();
  if (!shell) return;
  if (shell.dataset.locked === "1") return;

  const y = shell.scrollTop || 0;
  shell.dataset.locked = "1";
  shell.dataset.scrollTop = String(y);

  shell.style.overflow = "hidden";
  shell.style.height = "100dvh"; // keeps layout stable
}, []);

const unlockDashShellScroll = useCallback(() => {
  const shell = getDashShell();
  if (!shell) return;
  if (shell.dataset.locked !== "1") return;

  shell.dataset.locked = "0";
  shell.style.overflow = "";
  shell.style.height = "";

  const y = parseInt(shell.dataset.scrollTop || "0", 10) || 0;
  shell.dataset.scrollTop = "";
  shell.scrollTop = y;
}, []);



useEffect(() => {
  if (anyModalOpen) {
    lockBodyScroll();
    lockDashShellScroll();
  } else {
    unlockBodyScroll();
    unlockDashShellScroll();
  }

  return () => {
    unlockBodyScroll();
    unlockDashShellScroll();
  };
}, [
  anyModalOpen,
  lockBodyScroll,
  unlockBodyScroll,
  lockDashShellScroll,
  unlockDashShellScroll,
]);

  
  // default behavior:
  // - Courses collapsed by default
  // - Enrollment Progress collapsed by default
  // - Apprenticeship collapsed by default EXCEPT when something is pending review (then open)
  useEffect(() => {
    // Only reset when switching people
    setCoursesOpen(false);
    setProgressOpen(false);
    setApprenticeshipOpen(hasPendingApprReviewMemo);
    setApprUserToggled(false);
  }, [pid, hasPendingApprReviewMemo]);

  useEffect(() => {
    if (!apprUserToggled && apprenticeshipHasPending) {
      setApprenticeshipOpen(true); // auto-open when pending review exists
    }
  }, [apprenticeshipHasPending, apprUserToggled]);

  const refreshApprenticeship = useCallback(async () => {
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId) return;
    if (!isSelectedStudent) return;
    if (isPending) return;
    if (!isStaffViewer) return;

    setApprenticeshipErr("");
    setApprenticeshipLoading(true);
    try {
      const data = await ApprenticeshipAPI.fetchApprenticeshipForUser(studentId);
      setApprenticeship(data || null);
      setApprenticeshipBookingLink(String(data?.bookingLink || ""));
    } catch (e) {
      console.error(e);
      setApprenticeshipErr("Could not load apprenticeship submissions.");
    } finally {
      setApprenticeshipLoading(false);
    }
  }, [selectedPerson, isSelectedStudent, isPending, isStaffViewer]);

  // Live update apprenticeship info when staff is viewing this student
  useEffect(() => {
    if (!socket) return;
    if (!isSelectedStudent) return;
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId) return;

    const handleApprUpdate = (payload) => {
      const payloadStudentId = String(payload?.studentId || payload?.userId || "");
      if (!payloadStudentId || String(studentId) !== payloadStudentId) return;
      refreshApprenticeship();
    };

    socket.on("apprenticeship:updated", handleApprUpdate);
    return () => socket.off("apprenticeship:updated", handleApprUpdate);
  }, [socket, isSelectedStudent, selectedPerson, refreshApprenticeship]);

  // ✅ NEW: Unlock apprenticeship (confirm) — hidden once certification earned
  const handleUnlockApprenticeship = useCallback(async () => {
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId) return;

    const ok = window.confirm(
      "Unlock Apprenticeship Mode for this student?\n\nThey will be able to create and submit case studies for review."
    );
    if (!ok) return;

    try {
      setApprenticeshipSavingKey("unlock");
      await ApprenticeshipAPI.unlockApprenticeship(studentId, apprenticeshipBookingLink);
      alert("Apprenticeship mode unlocked ✅");
      await refreshApprenticeship();
    } catch (e) {
      console.error(e);
      alert("Failed to unlock apprenticeship mode.");
    } finally {
      setApprenticeshipSavingKey("");
    }
  }, [selectedPerson, refreshApprenticeship, apprenticeshipBookingLink]);

  const handleUpdateApprenticeshipLink = useCallback(async () => {
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId) return;

    try {
      setApprenticeshipSavingKey("booking-link");
      const updated = await ApprenticeshipAPI.updateBookingLink(studentId, apprenticeshipBookingLink);
      setApprenticeship(updated || null);
      alert("Booking link saved ✅");
    } catch (e) {
      console.error(e);
      alert("Failed to save booking link.");
    } finally {
      setApprenticeshipSavingKey("");
    }
  }, [selectedPerson, apprenticeshipBookingLink]);

  const apprenticeshipLinkIsSaved =
    String(apprenticeshipBookingLink || "").trim() === String(apprenticeshipBookingLinkSaved || "").trim();

  const handleSavePhotoPackageLink = useCallback(
    async (enrollmentId) => {
      if (!enrollmentId || !EnrollmentAPI?.updatePhotoPackageLink) return;

      try {
        setPhotoPackageSavingKey(String(enrollmentId));
        const updated = await EnrollmentAPI.updatePhotoPackageAdmin(enrollmentId, {
          deliveryLink: String(photoPackageDrafts?.[enrollmentId] || ""),
          paid: !!photoPackagePaidDrafts?.[enrollmentId],
        });
        const enrollment = updated?.data?.enrollment || updated?.enrollment || updated || null;
        if (enrollment?._id) {
          setPersonEnrollmentsById((prev) => {
            const list = Array.isArray(prev?.[pid]) ? prev[pid] : [];
            const nextList = list.map((item) =>
              String(item?._id || "") === String(enrollment._id) ? enrollment : item
            );
            return { ...(prev || {}), [pid]: nextList };
          });
        }
        alert("Photo package link saved ✅");
      } catch (e) {
        console.error(e);
        alert("Failed to save photo package link.");
      } finally {
        setPhotoPackageSavingKey("");
      }
    },
    [photoPackageDrafts, photoPackagePaidDrafts, pid, setPersonEnrollmentsById]
  );

  const handleTogglePhotoPackagePaid = useCallback(
    async (enrollmentId) => {
      if (!enrollmentId) return;

      const nextPaid = !photoPackagePaidDrafts?.[enrollmentId];

      setPhotoPackagePaidDrafts((prev) => ({
        ...(prev || {}),
        [enrollmentId]: nextPaid,
      }));

      try {
        setPhotoPackageSavingKey(`paid:${enrollmentId}`);
        const updated = await EnrollmentAPI.updatePhotoPackageAdmin(enrollmentId, {
          paid: nextPaid,
        });
        const enrollment = updated?.data?.enrollment || updated?.enrollment || updated || null;
        if (enrollment?._id) {
          setPersonEnrollmentsById((prev) => {
            const list = Array.isArray(prev?.[pid]) ? prev[pid] : [];
            const nextList = list.map((item) =>
              String(item?._id || "") === String(enrollment._id) ? enrollment : item
            );
            return { ...(prev || {}), [pid]: nextList };
          });
        }
      } catch (e) {
        console.error(e);
        setPhotoPackagePaidDrafts((prev) => ({
          ...(prev || {}),
          [enrollmentId]: !nextPaid,
        }));
        alert("Failed to update paid status.");
      } finally {
        setPhotoPackageSavingKey("");
      }
    },
    [EnrollmentAPI, photoPackagePaidDrafts, pid, setPersonEnrollmentsById]
  );

  const handleExtendEnrollmentAccess = useCallback(
    async (enrollmentId, courseTitle) => {
      if (!enrollmentId) return;

      const ok = window.confirm(
        `Extend ${courseTitle || "this course"} for 3 more months?\n\nThis will reopen access from the current expiration date and keep the student's progress intact.`
      );
      if (!ok) return;

      try {
        await EnrollmentAPI.extendEnrollmentAccess(enrollmentId, 3);
        await refreshSelectedStudentEnrollments();
        alert("Course access extended for 3 months ✅");
      } catch (e) {
        console.error(e);
        alert("Failed to extend course access.");
      }
    },
    [refreshSelectedStudentEnrollments]
  );

  const handleUpdateApprenticeshipSessionInfo = useCallback(
    async ({ index, sessionKey, clientName, sessionAt }) => {
      const studentId = selectedPerson?._id || selectedPerson?.id;
      if (!studentId) return;

      try {
        setApprenticeshipSessionSavingKey(`${index}:${sessionKey}`);
        const updated = await ApprenticeshipAPI.updateSessionInfoForStaff({
          studentId,
          index,
          session: sessionKey === "firstSession" ? "first" : "second",
          clientName,
          sessionAt,
        });
        setApprenticeship(updated || null);
      } catch (e) {
        console.error(e);
        alert("Failed to save session info.");
      } finally {
        setApprenticeshipSessionSavingKey("");
      }
    },
    [selectedPerson]
  );

  const sessionTimeOptions = useMemo(
    () => [
      { value: "10:00", label: "10 AM" },
      { value: "11:00", label: "11 AM" },
      { value: "12:00", label: "12 PM" },
      { value: "13:00", label: "1 PM" },
      { value: "14:00", label: "2 PM" },
      { value: "15:00", label: "3 PM" },
    ],
    []
  );

  // ✅ NEW: Remove apprenticeship (confirm) — hidden once certification earned
  const handleRemoveApprenticeship = useCallback(async () => {
    const studentId = selectedPerson?._id || selectedPerson?.id;
    if (!studentId) return;

    const ok = window.confirm(
      "Remove Apprenticeship Mode for this student?\n\nThis will lock it again and hide the student’s apprenticeship uploads.\n(Use only if you truly want to reset/disable apprenticeship.)"
    );
    if (!ok) return;

    try {
      setApprenticeshipSavingKey("remove");
      // POST /api/apprenticeships/:studentId/remove (or DELETE /api/apprenticeships/:studentId)
      await ApprenticeshipAPI.removeApprenticeship(studentId);
      alert("Apprenticeship mode removed (locked) ✅");
      await refreshApprenticeship();
    } catch (e) {
      console.error(e);
      alert("Failed to remove apprenticeship mode.");
    } finally {
      setApprenticeshipSavingKey("");
    }
  }, [selectedPerson, refreshApprenticeship, apprenticeshipBookingLink]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedPerson?._id && !selectedPerson?.id) return;
      if (!isSelectedStudent) return;
      if (isPending) return;
      if (!isStaffViewer) return;

      setApprenticeshipErr("");
      setApprenticeshipLoading(true);
      try {
        const studentId = selectedPerson?._id || selectedPerson?.id;
        const data = await ApprenticeshipAPI.fetchApprenticeshipForUser(studentId);
        if (!cancelled) setApprenticeship(data || null);
      } catch (e) {
        console.error(e);
        if (!cancelled) setApprenticeshipErr("Could not load apprenticeship submissions.");
      } finally {
        if (!cancelled) setApprenticeshipLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedPerson, isSelectedStudent, isPending, isStaffViewer]);

  const reviewApprenticeship = useCallback(
    async ({ index, session, status }) => {
      const studentId = selectedPerson?._id || selectedPerson?.id;
      if (!studentId) return;

      try {
        const reviewerNotes =
          status === "changes_requested"
            ? "Your photos have not been approved. Please submit new photos for approval."
            : "";

        const updated = await ApprenticeshipAPI.reviewApprenticeshipSession({
          studentId,
          index,
          session, // "first" | "second"
          status, // "approved" | "changes_requested"
          reviewerNotes,
        });

        setApprenticeship(updated || null);
      } catch (e) {
        console.error(e);
        alert("Could not update this submission.");
      }
    },
    [selectedPerson]
  );

  // ✅ NEW: STAFF delete case study (hard reset)
  const staffDeleteCaseStudy = useCallback(
    async (index) => {
      const studentId = selectedPerson?._id || selectedPerson?.id;
      if (!studentId) return;

      const ok = window.confirm(`Delete Case Study ${index + 1}? This removes it so the student can start over.`);
      if (!ok) return;

      try {
        setApprenticeshipSavingKey(`staff-delete:${index}`);
        // DELETE /api/apprenticeships/:studentId/case-studies/:index
        const updated = await ApprenticeshipAPI.staffDeleteCaseStudy(studentId, index);
        setApprenticeship(updated || null);
      } catch (e) {
        console.error(e);
        alert("Could not delete this case study.");
      } finally {
        setApprenticeshipSavingKey("");
      }
    },
    [selectedPerson]
  );

  function format9(digits) {
  const s = String(digits).replace(/\D/g, "").slice(0, 10);
  if (s.length <= 3) return s;
  if (s.length <= 6) return `${s.slice(0,3)}-${s.slice(3)}`;
  return `(${s.slice(0,3)}) ${s.slice(3,6)}-${s.slice(6)}`;
}

  const queued = (queuedCoursesByPersonId?.[pid] || []).slice(0, 3);
  const queuedSet = new Set(queued);

  const pendingRemovals = (pendingRemovalsByPersonId?.[pid] || []).slice(0, 3);
  const pendingRemovalSet = new Set(pendingRemovals);

  const scrollResetKey = useMemo(() => {
    const caseCount = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies.length : 0;
    return `${pid}:${sortedEnrollments.length}:${caseCount}:${queued.length}:${pendingRemovals.length}`;
  }, [pid, sortedEnrollments.length, apprenticeship, queued.length, pendingRemovals.length]);

  useEffect(() => {
    if (!panelRef.current) return;
    panelRef.current.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [scrollResetKey]);

  // ✅ now safe to return early
  if (!selectedPerson) return null;

  const first = selectedPerson.firstName || selectedPerson.first_name || "";
  const last = selectedPerson.lastName || selectedPerson.last_name || "";
  const email = selectedPerson.email || "";
  const address = selectedPerson.address || "";
  const phone = format9(selectedPerson.phone) || "";
  const avatar =
    selectedPerson.profilePic ||
    selectedPerson.profilePicUrl ||
    selectedPerson.avatarUrl ||
    selectedPerson.imageUrl ||
    null;

  const recipientId = selectedPerson._id || selectedPerson.id;

const personScheduleMap = typeof getScheduleMapForPerson === "function"
  ? getScheduleMapForPerson(selectedPerson)
  : {};


  const scheduleCompleteForCourse = (courseKey) => {
    const sched = personScheduleMap?.[courseKey] || {};
    if (!sched?.startDate) return false;
    if (requiresModelForCourseKey(courseKey) && !sched?.modelTimeSlot) return false;
    return true;
  };

  const enrolledCourseKeys = (enrollmentsForPerson || []).map(getCourseKeyFromEnrollment).filter(Boolean);
  const enrolledSet = new Set(enrolledCourseKeys);

  const queuedScheduleProblems = validateScheduleForKeys(personScheduleMap, queued);
  const queuedReady = queued.length > 0 && queuedScheduleProblems.length === 0;

  const availableCourses = (gmCourses || []).filter((c) => {
    if (!c?.key) return false;
    if (queuedSet.has(c.key)) return false;
    if (!isPendingStudent && enrolledSet.has(c.key)) return false;
    return true;
  });

  const openScheduleModalForCourse = (courseKey) => {
    if (!selectedPerson?._id || !courseKey) return;
    effectiveSetModalCourseKey(courseKey);
    effectiveSetIsEnrollModalOpen(true);
  };

  const clearScheduleForCourse = (courseKey) => {
    if (!selectedPerson?._id || !courseKey) return;
    setScheduleByPersonId((prev) => {
      const copy = { ...(prev || {}) };
      const p = copy[selectedPerson._id] ? { ...copy[selectedPerson._id] } : {};
      if (p[courseKey]) delete p[courseKey];
      copy[selectedPerson._id] = p;
      return copy;
    });
  };

const closeEnrollModal = ({ clear } = { clear: false }) => {
  if (clear && effectiveModalCourseKey) clearScheduleForCourse(effectiveModalCourseKey);
  effectiveSetIsEnrollModalOpen(false);
  effectiveSetModalCourseKey(null);
};

  const queueCourseForPerson = (courseKey) => {
    if (!pid || !courseKey) return;
    setQueuedCoursesByPersonId((prev) => {
      const next = { ...(prev || {}) };
      const current = new Set(next[pid] || []);
      if (current.size >= 3) return next;
      current.add(courseKey);
      next[pid] = Array.from(current);
      return next;
    });
  };

  const unqueueCourseForPerson = (courseKey) => {
    if (!pid || !courseKey) return;
    setQueuedCoursesByPersonId((prev) => {
      const next = { ...(prev || {}) };
      const current = new Set(next[pid] || []);
      current.delete(courseKey);
      next[pid] = Array.from(current);
      return next;
    });
    clearScheduleForCourse(courseKey);
  };

  const toggleRemovalForPerson = (courseKey) => {
    if (!pid || !courseKey) return;
    setPendingRemovalsByPersonId((prev) => {
      const next = { ...(prev || {}) };
      const current = new Set(next[pid] || []);
      if (current.has(courseKey)) current.delete(courseKey);
      else current.add(courseKey);
      next[pid] = Array.from(current);
      return next;
    });
  };

  const handleModalChange = (courseKey, nextPartial) => {
    if (!pid || !courseKey) return;
    setScheduleByPersonId((prev) => {
      const next = { ...(prev || {}) };
      const personMap = next[pid] ? { ...next[pid] } : {};
      personMap[courseKey] = {
        ...(personMap[courseKey] || {}),
        ...(nextPartial || {}),
      };
      next[pid] = personMap;
      return next;
    });
  };

const onSaveScheduleForModal = () => {
  const courseKey = effectiveModalCourseKey;
  if (!courseKey) return;

  if (!scheduleCompleteForCourse(courseKey)) {
    alert("Please choose Day 1 date (and model time when required).");
    return;
  }

  queueCourseForPerson(courseKey);
  closeEnrollModal();
};


  const getSubmissionStatusForCourse = (courseKey) => {
    const enr = (enrollmentsForPerson || []).find((e) => getCourseKeyFromEnrollment(e) === courseKey) || null;

    const modelReq = enr?.modelRequirement || null;
    const modelSub = enr?.modelSubmission || null;

    const modelRequired = !!modelReq?.required;
    const modelStatus = modelReq?.status || (modelRequired ? "needed" : "");
    const modelDate = modelReq?.date ? new Date(modelReq.date) : null;
    const modelTime = modelReq?.timeSlot || "";
    const modelSubmittedAt = modelSub?.submittedAt ? new Date(modelSub.submittedAt) : null;

    const globalBB = getGlobalBloodborneFromUser(selectedPerson);

    return {
      model: {
        required: modelRequired,
        status: modelRequired ? modelStatus : "",
        timeSlot: modelTime,
        date: modelDate,
        submittedAt: modelSubmittedAt,
        name: modelSub?.name || "",
        phone: modelSub?.phone || "",
        email: modelSub?.email || "",
      },
      bloodborne: globalBB,
    };
  };

  const requestConfirmApplyChanges = () => {
    const added = queued.slice(0, 3);
    const removed = isSelectedStudent && !isPending ? pendingRemovals.slice(0, 3) : [];
    if (!added.length && !removed.length) return;

    const pretty = (key) => resolveCourseFromKey(key)?.title || key;

    const lines = [];
    if (added.length) lines.push(`Add: ${added.map(pretty).join(", ")}`);
    if (removed.length) lines.push(`Remove: ${removed.map(pretty).join(", ")}`);
    lines.push("");
    lines.push(isPendingStudent ? "Approve and enroll this student now?" : "Apply these enrollment changes now?");

    effectiveSetCourseConfirmText(lines.join("\n"));
    effectiveSetShowCourseConfirm(true);

    effectiveSetPendingCourseCommit(() => async () => {
      const id = selectedPerson._id || selectedPerson.id;
      if (!id) return;

      try {
        setIsCourseSaving(true);

        if (isPendingStudent && approveStudent) {
          if (!queued.length) return;

          const probs = validateScheduleForKeys(getScheduleMapForPerson(selectedPerson), queued);
          if (probs.length) {
            alert("Please set class date (and model time when required) for all selected courses.");
            return;
          }

          await approveStudent({
            studentId: id,
            courseKeys: queued.slice(0, 3),
            scheduleByCourseKey: getScheduleMapForPerson(selectedPerson),
          });

          // ✅ instant UI flip while fetches run
setSelectedPerson((prev) => {
  if (!prev) return prev;
  const prevId = String(prev._id || prev.id || "");
  if (prevId !== String(id)) return prev;
  return { ...prev, _isPendingStudent: false, status: "active", isApproved: true };
});


          

          const schedMap = getScheduleMapForPerson(selectedPerson);
          for (const key of queued.slice(0, 3)) {
            const sched = schedMap?.[key] || {};
            await EnrollmentAPI.createOrUpdateEnrollment({
              studentId: id,
              courseKey: key,
              startDate: sched.startDate,
              modelTimeSlot: sched.modelTimeSlot || "",
            });
          }

          

    await Promise.all([
  fetchPendingStudents ? fetchPendingStudents() : Promise.resolve(),
  fetchActiveStudents ? fetchActiveStudents() : Promise.resolve(),
]);

refreshSelectedPersonRef && refreshSelectedPersonRef(id);



          if (fetchEnrollmentsForUser) {
            const res = await fetchEnrollmentsForUser(id);
            const enrollmentsArr = Array.isArray(res)
              ? res
              : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.enrollments)
              ? res.enrollments
              : [];
            setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(id)]: enrollmentsArr }));
          }

          setQueuedCoursesByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
          setPendingRemovalsByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
          return;
        }

        if (isPendingMaster && approveMaster) {
          await approveMaster(id);
          fetchPendingMasters && fetchPendingMasters();
          fetchMasters && fetchMasters();
          fetchActiveMasters && fetchActiveMasters();
          return;
        }

        if (isSelectedStudent && !isPending) {
          if (onRemoveUserFromCourse) {
            for (const key of pendingRemovals.slice(0, 3)) {
              await onRemoveUserFromCourse(id, key);
            }
          }

          if (queued.length) {
            const probs = validateScheduleForKeys(getScheduleMapForPerson(selectedPerson), queued);
            if (probs.length) {
              alert("Please set class date (and model time when required) for each added course.");
              return;
            }

            if (onAssignUserToCourse) {
              for (const key of queued) await onAssignUserToCourse(id, key);
            }

            const schedMap = getScheduleMapForPerson(selectedPerson);
            for (const key of queued) {
              const sched = schedMap?.[key] || {};
              await EnrollmentAPI.createOrUpdateEnrollment({
                studentId: id,
                courseKey: key,
                startDate: sched.startDate,
                modelTimeSlot: sched.modelTimeSlot || "",
              });
            }
          }

          if (fetchEnrollmentsForUser) {
            const res = await fetchEnrollmentsForUser(id);
            const enrollmentsArr = Array.isArray(res)
              ? res
              : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.enrollments)
              ? res.enrollments
              : [];
            setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(id)]: enrollmentsArr }));
          }

          setQueuedCoursesByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
          setPendingRemovalsByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
        }
      } catch (err) {
        console.error("Error applying enrollment changes:", err);
     } finally {
  if (typeof setIsCourseSaving === "function") setIsCourseSaving(false);
  effectiveSetShowCourseConfirm(false);
  effectiveSetCourseConfirmText("");
  effectiveSetPendingCourseCommit(null);
}

    });
  };

  const cancelCourseConfirm = () => {
    effectiveSetShowCourseConfirm(false);
    effectiveSetCourseConfirmText("");
    effectiveSetPendingCourseCommit(null);
  };

  const runCourseConfirm = async () => {
if (typeof effectivePendingCourseCommit === "function") {
  await effectivePendingCourseCommit();
} else {
  cancelCourseConfirm();
}
  }

  const showApplyChangesButton =
    (isPendingStudent && queued.length > 0 && queuedReady) ||
    (!isPending && isSelectedStudent && (queued.length > 0 || pendingRemovals.length > 0));

  const handleApproveClick = async () => {
    if (!isPendingMaster) return requestConfirmApplyChanges();

    const id = selectedPerson._id || selectedPerson.id;
    if (!id) return;

    try {
      await approveMaster(id);
      fetchPendingMasters && fetchPendingMasters();
      fetchMasters && fetchMasters();
      fetchActiveMasters && fetchActiveMasters();
    } catch (err) {
      console.error("Error approving master:", err);
    } finally {
      onClose();
    }
  };

  const queuedCourseMeta = queued.map(resolveCourseFromKey).filter(Boolean);

  const handleCourseChipClick = (courseKey) => {
    if (!courseKey) return;
    if (queuedSet.has(courseKey)) return unqueueCourseForPerson(courseKey);
    if (queued.length >= 3) return;
    openScheduleModalForCourse(courseKey);
  };


  

  return (
    <div
      ref={panelRef}
      className="dash-panel dash-person-panel"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dash-person-header dash-person-header-sticky">
<button
  type="button"
  className="dash-person-close-x"
  onClick={onClose}
  aria-label="Close"
  title="Close"
>
  ×
</button>

        <div className="dash-avatar-orb dash-avatar-orb-header">
          <div className="dash-avatar-face">
            {avatar ? (
              <img src={avatar} alt={first || "Artist"} />
            ) : (
              <span className="dash-avatar-initials">{(first || "A").charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="dash-avatar-ring" />
        </div>

        <div className="dash-person-header-text">
          <div className="dash-person-name">{`${first || "Artist"}${last ? ` ${last}` : ""}`}</div>

          <div className="dash-person-meta">
            {isSelectedMaster ? "Master Artist" : "Student"}
            {(isPendingStudent || isPendingMaster) && (
              <span className="dash-badge-pill dash-badge-pill-warn">Pending</span>
            )}
          </div>

          {email && (
            <div className="dash-person-meta dash-person-email-row">
              <button
                type="button"
                className="dash-person-email-copy"
                onClick={handleCopyEmail}
                title="Copy email"
              >
                <span className="dash-person-email-copy-text">{emailCopied ? "Copied!" : email}</span>
                <span className="dash-person-email-copy-hint">
                  {emailCopied ? "Done" : "Copy"}
                </span>
              </button>
            </div>
          )}
          {phone && <div className="dash-person-meta dash-person-email">{phone}</div>}
          {address && <div className="dash-person-meta dash-person-email">{address.street}<br/>{address.city} {address.state} {address.zip}</div>}


            {/* 2) Messages section */}
<div className="dash-person-header-block">
  <Link
    to="/admin/messages"
    state={{
      recipientId,
      recipientFirstName: first,
      recipientLastName: last,
      recipientAvatarUrl: avatar,
    }}
    className="dash-open-messages-btn"
  >
    Open messages <i className="fa-solid fa-paper-plane" />
  </Link>
</div>


          <div className="dash-person-header-actions">
{/* 3) Apprenticeship controls section (staff) */}
{isSelectedStudent && !isPending && isStaffViewer && !certificationEarned && (
  <div className="dash-person-header-block">
    <button
      type="button"
      className="dash-inline-admin-toggle"
      onClick={() => setApprenticeshipAdminOpen((v) => !v)}
      aria-expanded={apprenticeshipAdminOpen}
    >
      <span className="dash-inline-admin-toggle-left">
        <span className="dash-section-caret">{apprenticeshipAdminOpen ? "▾" : "▸"}</span>
        <span>{apprenticeship?.unlocked ? "Apprenticeship Link For Student" : "Unlock Apprenticeship Mode"}</span>
      </span>
      <span className="dash-inline-admin-toggle-sub">
        {apprenticeship?.unlocked ? "Manage booking link and apprenticeship access." : "Add the booking link and unlock when ready."}
      </span>
    </button>

    {apprenticeshipAdminOpen && (
      <>
        {!apprenticeship?.unlocked ? (
          <div className="dash-apprenticeship-unlock-stack">
            <div className="dash-link-label-row">
              <label className="dash-apprenticeship-link-label" htmlFor="apprenticeship-booking-link">
                Booking link for student (optional)
              </label>
              {apprenticeshipLinkIsSaved && apprenticeshipBookingLinkSaved ? (
                <span className="dash-link-saved-pill">
                  <i className="fa-solid fa-check" /> Saved
                </span>
              ) : null}
            </div>
            <input
              id="apprenticeship-booking-link"
              type="url"
              className="dash-apprenticeship-link-input"
              placeholder={apprenticeshipBookingLinkSaved || "https://your-booking-link.com"}
              value={apprenticeshipBookingLink}
              onChange={(e) => setApprenticeshipBookingLink(e.target.value)}
            />

            <button
              type="button"
              className="dash-apprenticeship-unlock-btn"
              onClick={handleUnlockApprenticeship}
              disabled={apprenticeshipSavingKey === "unlock"}
              style={{ opacity: apprenticeshipSavingKey === "unlock" ? 0.65 : 1 }}
            >
              {apprenticeshipSavingKey === "unlock" ? "Unlocking…" : "Unlock apprenticeship mode"}
            </button>
          </div>

        ) : (
          <div className="dash-apprenticeship-unlock-stack">
            <div className="dash-apprenticeship-admin-card">
              <div className="dash-link-label-row">
                <label className="dash-apprenticeship-link-label" htmlFor="apprenticeship-booking-link">
                  Booking link for student
                </label>
                {apprenticeshipLinkIsSaved && apprenticeshipBookingLinkSaved ? (
                  <span className="dash-link-saved-pill">
                    <i className="fa-solid fa-check" /> Saved
                  </span>
                ) : null}
              </div>
              <input
                id="apprenticeship-booking-link"
                type="url"
                className="dash-apprenticeship-link-input"
                placeholder={apprenticeshipBookingLinkSaved || "https://your-booking-link.com"}
                value={apprenticeshipBookingLink}
                onChange={(e) => setApprenticeshipBookingLink(e.target.value)}
              />

              <div className="dash-apprenticeship-link-actions">
                <button
                  type="button"
                  className="dash-chip-button"
                  onClick={handleUpdateApprenticeshipLink}
                  disabled={apprenticeshipSavingKey === "booking-link"}
                >
                  {apprenticeshipSavingKey === "booking-link" ? "Saving…" : "Save booking link"}
                </button>

                <button
                  type="button"
                  className="dash-apprenticeship-danger-btn"
                  onClick={handleRemoveApprenticeship}
                  disabled={apprenticeshipSavingKey === "remove"}
                  style={{ opacity: apprenticeshipSavingKey === "remove" ? 0.65 : 1 }}
                  title="Locks apprenticeship mode again"
                >
                  {apprenticeshipSavingKey === "remove" ? "Removing…" : "Remove apprenticeship mode"}
                </button>
              </div>
            </div>
          </div>

        )}
      </>
    )}
  </div>
)}

{isSelectedStudent && !isPending && isStaffViewer && optedInPhotoPackageEnrollments.length > 0 && (
  <div className="dash-person-header-block">
    <button
      type="button"
      className="dash-inline-admin-toggle"
      onClick={() => setPhotoPackageAdminOpen((v) => !v)}
      aria-expanded={photoPackageAdminOpen}
    >
      <span className="dash-inline-admin-toggle-left">
        <span className="dash-section-caret">{photoPackageAdminOpen ? "▾" : "▸"}</span>
        <span>Photo Package</span>
      </span>
      <span className="dash-inline-admin-toggle-sub">
        {optedInPhotoPackageCourseSummary || "Manage paid status and delivery links for opted-in courses."}
      </span>
    </button>

    {photoPackageAdminOpen && (
      <div className="dash-photo-package-admin-stack">
        <div className="dash-photo-package-admin-copy">
          This student opted in. Add or update the delivery link for each course below.
        </div>

        {optedInPhotoPackageEnrollments.map((enr) => {
          const enrollmentId = String(enr?._id || "");
          const courseTitle = enr?.course?.title || getCourseKeyFromEnrollment(enr) || "Course";
          const saving = photoPackageSavingKey === enrollmentId;
          const paidSaving = photoPackageSavingKey === `paid:${enrollmentId}`;
          const savedLink = String(enr?.photoPackage?.deliveryLink || "").trim();
          const savedPaid = !!enr?.photoPackage?.paid;
          const draftLink = String(photoPackageDrafts?.[enrollmentId] || "").trim();
          const draftPaid = !!photoPackagePaidDrafts?.[enrollmentId];
          const photoPackageIsSaved = draftLink === savedLink && draftPaid === savedPaid;

          return (
            <div key={enrollmentId} className="dash-photo-package-admin-card">
              <div className="dash-photo-package-admin-head">
                <div className="dash-photo-package-admin-course">{courseTitle}</div>
                {photoPackageIsSaved && (savedLink || savedPaid) ? (
                  <span className="dash-link-saved-pill">
                    <i className="fa-solid fa-check" /> Saved
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className={`dash-photo-package-paid-toggle ${
                  photoPackagePaidDrafts?.[enrollmentId] ? "is-paid" : ""
                }`}
                onClick={() => handleTogglePhotoPackagePaid(enrollmentId)}
                disabled={paidSaving}
              >
                <span className="dash-photo-package-paid-toggle-icon">
                  {photoPackagePaidDrafts?.[enrollmentId] ? <i className="fa-solid fa-check" /> : <span className="dash-photo-package-paid-toggle-empty" />}
                </span>
                <span>
                  {paidSaving ? "Saving…" : photoPackagePaidDrafts?.[enrollmentId] ? "Paid" : "Mark paid"}
                </span>
              </button>
              <label className="dash-apprenticeship-link-label" htmlFor={`photo-package-link-${enrollmentId}`}>
                Photo package link
              </label>
              <input
                id={`photo-package-link-${enrollmentId}`}
                type="url"
                className="dash-apprenticeship-link-input"
                placeholder="https://dropbox.com/... or https://drive.google.com/..."
                value={photoPackageDrafts?.[enrollmentId] || ""}
                onChange={(e) =>
                  setPhotoPackageDrafts((prev) => ({
                    ...(prev || {}),
                    [enrollmentId]: e.target.value,
                  }))
                }
              />
              <button
                type="button"
                className="dash-chip-button"
                onClick={() => handleSavePhotoPackageLink(enrollmentId)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save photo package"}
              </button>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}

{/* 4) Pending approve/enroll section (keep separate too, if you want it below messages) */}
{(isPendingStudent || isPendingMaster) && (
  <div className="dash-person-header-block">
    <button
      type="button"
      className="dash-chip-button dash-header-wide-cta"
      onClick={handleApproveClick}
      disabled={isCourseSaving || (isPendingStudent && (!queued.length || !queuedReady))}
      style={{ opacity: isCourseSaving || (isPendingStudent && (!queued.length || !queuedReady)) ? 0.6 : 1 }}
      title={isPendingStudent && (!queued.length || !queuedReady) ? "Select a course, set class date + model time, then approve." : ""}
    >
      {isPendingStudent ? "Approve & Enroll" : "Approve & Assign Courses"}
    </button>
  </div>
)}

           
</div></div>
      </div>

      {/* COURSES */}
      <div className="dash-person-section dash-person-section--courses">
  <div
  className="dash-person-section-header dash-section-toggle"
  role="button"
  tabIndex={0}
  onClick={(e) => {
    e.stopPropagation();
    setCoursesOpen((v) => !v);
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      setCoursesOpen((v) => !v);
    }
  }}
  aria-expanded={coursesOpen}
  style={{ cursor: "pointer", userSelect: "none" }}
>
  <span className="dash-section-toggle-left">
    <span className="dash-section-caret">{coursesOpen ? "▾" : "▸"}</span>
    <span>Enrolled Courses</span>
  </span>

  {coursesOpen ? (
    <span className="dash-person-section-sub">
      {isPendingStudent
        ? "Click a course to set class date + model time. Saving will queue it (and hide it from the available list)."
        : isSelectedStudent
        ? "Add courses (click to schedule) or remove courses (confirm). Then apply changes."
        : "Tap up to three courses below."}
    </span>
  ) : enrolledCourseKeys.length ? (
    <span className="dash-person-progress-pills">
      {enrolledCourseKeys.map((ck) => {
        const meta = resolveCourseFromKey(ck);
        return (
          <span key={ck} className="dash-mini-chip">
            {meta?.title || ck}
          </span>
        );
      })}
    </span>
  ) : (
    <span className="dash-person-section-sub">No courses enrolled yet.</span>
  )}
</div>

        {coursesOpen && (
          <>
            {isSelectedStudent && !isPending && (
              <div style={{ marginTop: 10 }}>
                <div className="dash-person-section-sub" style={{ marginBottom: 6 }}>
                  Current enrollments
                </div>

                {!enrolledCourseKeys.length ? (
                  <p className="dash-empty-text">No enrollment records found yet for this student.</p>
                ) : (
                  <ul className="dash-person-course-list">
                    {enrolledCourseKeys.map((ck) => {
                      const meta = resolveCourseFromKey(ck);
                      const enrollmentRecord = sortedEnrollments.find((enr) => getCourseKeyFromEnrollment(enr) === ck);
                      const isMarked = pendingRemovalSet.has(ck);
                      const expired = isEnrollmentExpired(enrollmentRecord);
                      const expiryDate = enrollmentRecord?.expiryDate ? formatDateUTC(enrollmentRecord.expiryDate) : "";
                      return (
                        <li
                          key={ck}
                          className="dash-person-course-item"
                          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span>{meta?.title || ck}</span>
                            {!!expiryDate && (
                              <span className="dash-course-meta" style={{ margin: 0 }}>
                                Access until: <span className="dash-course-meta-strong">{expiryDate}</span>
                              </span>
                            )}
                            {expired && (
                              <span className="dash-course-note" style={{ margin: 0 }}>
                                Expired. Progress is saved.
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {expired && enrollmentRecord?._id ? (
                              <button
                                type="button"
                                className="dash-chip-button"
                                style={{ padding: "8px 10px", fontSize: 12 }}
                                onClick={() =>
                                  handleExtendEnrollmentAccess(
                                    enrollmentRecord._id,
                                    meta?.title || ck
                                  )
                                }
                              >
                                Extend for 3 months
                              </button>
                            ) : null}

                            <button
                              type="button"
                              className="dash-chip-button dash-chip-button-secondary"
                              style={{ padding: "8px 10px", fontSize: 12, opacity: isMarked ? 0.85 : 1 }}
                              onClick={() => toggleRemovalForPerson(ck)}
                            >
                              {isMarked ? "Undo remove" : "Remove"}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <div className="dash-person-section-sub" style={{ marginBottom: 6 }}>
                Queued to {isPendingStudent ? "enroll" : "add"}: {queued.length} / 3
              </div>

              {!queuedCourseMeta.length ? (
                <p className="dash-empty-text" style={{ marginTop: 8 }}>
                  No courses queued yet.
                </p>
              ) : (
                <ul className="dash-person-course-list">
                  {queuedCourseMeta.map((c) => {
                    const sched = personScheduleMap?.[c.key] || {};
                    return (
                     <li className="dash-person-course-item dash-queued-row">
  <span className="dash-queued-title">{c.title}</span>

  <div className="dash-queued-actions">
    <span className="dash-person-course-tag">{c.track || "Prep"}</span>

    {sched?.startDate && (
      <span className="dash-person-course-tag dash-queued-date">
        {formatDateUTC(sched.startDate)}
        {sched.modelTimeSlot ? ` • ${sched.modelTimeSlot}` : ""}
      </span>
    )}

    <button
      type="button"
      className="dash-chip-button dash-chip-button-secondary dash-queued-btn"
      onClick={() => openScheduleModalForCourse(c.key)}
      title="Edit date/time"
    >
      Edit
    </button>

    <button
      type="button"
      className="dash-chip-button dash-chip-button-secondary dash-queued-btn"
      onClick={() => unqueueCourseForPerson(c.key)}
    >
      Unselect
    </button>
  </div>
</li>

                    );
                  })}
                </ul>
              )}

              {queued.length > 0 && queuedScheduleProblems.length > 0 && (
                <div className="dash-course-note" style={{ marginTop: 10 }}>
                  Missing scheduling for:{" "}
                  {queuedScheduleProblems.map((p) => resolveCourseFromKey(p.key)?.title || p.key).join(", ")}.
                </div>
              )}
            </div>

            {gmCourses && gmCourses.length > 0 && (
              <div className="dash-person-available-courses" style={{ marginTop: 16 }}>
                <div className="dash-person-section-sub">Available courses</div>
                <div className="dash-person-course-chips">
                  {availableCourses.map((course) => {
                    const key = course.key;
                    const title = course.title || "Untitled Course";
                    const disabled = isCourseSaving || queued.length >= 3;

                    return (
                      <button
                        key={key}
                        type="button"
                        className="dash-course-chip"
                        onClick={() => handleCourseChipClick(key)}
                        disabled={disabled}
                        title={queued.length >= 3 ? "You can select up to 3 courses" : "Click to set class date + model time"}
                        style={{ opacity: disabled ? 0.65 : 1 }}
                      >
                        {title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isSelectedStudent && showApplyChangesButton && (
              <div className="dash-person-actions-row" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="dash-chip-button"
                  onClick={requestConfirmApplyChanges}
                  disabled={isCourseSaving || (isPendingStudent && (!queued.length || !queuedReady))}
                  style={{ opacity: isCourseSaving || (isPendingStudent && (!queued.length || !queuedReady)) ? 0.6 : 1 }}
                >
                  {isCourseSaving ? "Saving..." : isPendingStudent ? "Approve & Enroll now" : "Confirm & Apply Changes"}
                </button>

                {!isCourseSaving && (
                  <button
                    type="button"
                    className="dash-chip-button dash-chip-button-secondary"
                    onClick={() => {
                      setQueuedCoursesByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
                      setPendingRemovalsByPersonId((prev) => ({ ...(prev || {}), [pid]: [] }));
                      closeEnrollModal();
                    }}
                  >
                    Discard staged changes
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ENROLLMENT PROGRESS */}
      {isSelectedStudent && !isPending && (
        <div className="dash-person-section dash-person-section--progress">
          <div
            className="dash-person-section-header dash-section-toggle"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setProgressOpen((v) => !v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setProgressOpen((v) => !v);
              }
            }}
            aria-expanded={progressOpen}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <span className="dash-section-toggle-left">
              <span className="dash-section-caret">{progressOpen ? "▾" : "▸"}</span>
              <span>Prep Work Progress</span>
            </span>
            {progressOpen ? (
              <span className="dash-person-section-sub">Prep progress, current section, and submission statuses.</span>
            ) : sortedEnrollments.length ? (
              <span className="dash-person-progress-pills">
                {sortedEnrollments.map((enr) => {
                  const key = getCourseKeyFromEnrollment(enr);
                  const meta = resolveCourseFromKey(key) || { key, title: key };
                  const pct = Math.max(0, Math.min(100, Number(enr?.progressPercent || 0)));
                  return (
                    <span
                      key={enr._id || key}
                      className={`dash-mini-chip ${pct === 100 ? "dash-mini-chip-complete" : ""}`}
                    >
                      {meta.title || key}: {pct}%
                    </span>
                  );
                })}
              </span>
            ) : (
              <span className="dash-person-section-sub">Prep progress, current section, and submission statuses.</span>
            )}
          </div>

          {progressOpen && (
            <>
              {!enrollmentsForPerson.length ? (
                <p className="dash-empty-text">No enrollment records found yet for this student.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {sortedEnrollments.map((enr) => {
                    const key = getCourseKeyFromEnrollment(enr);
                    const meta = resolveCourseFromKey(key) || { key, title: key, track: "" };
                    const pct = Math.max(0, Math.min(100, Number(enr?.progressPercent || 0)));
                    const sectionTitle = enr?.currentSectionTitle || "";
                    const startDate = enr?.startDate ? new Date(enr.startDate) : null;
                    const expiryDate = enr?.expiryDate ? new Date(enr.expiryDate) : null;
                    const expired = isEnrollmentExpired(enr);

                    const subs = getSubmissionStatusForCourse(key);
                    const model = subs.model;
                    const bb = subs.bloodborne;

                    const stats = computeQuizAttemptStats(enr);
                    const last = stats.lastAttempt;
                    const lastScore = last ? `${last.correctCount ?? 0}/${last.total ?? 0}` : "";

                    return (
                      <div key={enr._id || key} className="dash-course-card" style={{ padding: 16 }}>
                        <div className="dash-course-title">{meta.title}</div>
                        {meta.track && <div className="dash-course-label">{meta.track}</div>}
                        {startDate && <div className="dash-course-meta">Class start: {formatDateUTC(startDate)}</div>}
                        {expiryDate && <div className="dash-course-meta">Access until: {formatDateUTC(expiryDate)}</div>}
                        {expired ? (
                          <div className="dash-course-note" style={{ marginTop: 8 }}>
                            This course is expired. The student's progress is saved.
                          </div>
                        ) : null}
                        {expired && enr?._id ? (
                          <button
                            type="button"
                            className="dash-chip-button"
                            onClick={() => handleExtendEnrollmentAccess(enr._id, meta.title || key)}
                            style={{ marginTop: 10 }}
                          >
                            Extend for 3 months
                          </button>
                        ) : null}

                        <div className="dash-course-meta">
                          Current section:{" "}
                          <span className="dash-course-meta-strong">{sectionTitle || "Not started"}</span>
                        </div>

                        <div className="dash-course-progress">
                          <div
                            className="dash-course-progress-bar"
                            role="progressbar"
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <div className="dash-course-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="dash-course-progress-text">{pct}% complete</span>
                        </div>

                        {/* ✅ Quiz attempts + per-section history */}
                        {(() => {
                          if (!stats.totalAttempts) {
                            return (
                              <div className="dash-course-note" style={{ marginTop: 10 }}>
                                No quiz attempts yet for this course.
                              </div>
                            );
                          }

                          return (
                            <div style={{ marginTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
                              <div className="dash-course-meta" style={{ marginBottom: 6 }}>
                                <span className="dash-course-meta-strong">Quiz Attempts</span>
                                <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.85 }}>
                                  Total: {stats.totalAttempts}
                                </span>
                                <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.85 }}>
                                  Failed: {stats.failedAttempts}
                                </span>
                                <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.85 }}>
                                  Sections passed: {stats.passedSections}
                                </span>
                              </div>

                              {last?.submittedAt && (
                                <div className="dash-course-meta">
                                  Last attempt:{" "}
                                  <span className="dash-course-meta-strong">
                                    {formatDateTimeLocal(last.submittedAt)} • {lastScore} •{" "}
                                    {last.isPerfect ? "✅ Perfect" : "❌ Failed"}
                                  </span>
                                </div>
                              )}

                              <details style={{ marginTop: 8 }}>
                                <summary className="dash-course-meta" style={{ cursor: "pointer" }}>
                                  View per-section attempt history →
                                </summary>

                                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                                  {stats.sections
                                    .filter((s) => s.attempts.length > 0)
                                    .map((s) => {
                                      const lastA = s.lastAttempt;
                                      const score = lastA ? `${lastA.correctCount ?? 0}/${lastA.total ?? 0}` : "—";
                                      const label = s.quizPassed ? "✅ Passed" : s.failures > 0 ? "❌ Not passed yet" : "—";

                                      const displayTitle = s.sectionTitle
                                        ? s.sectionTitle.length > 34
                                          ? s.sectionTitle.slice(0, 34) + "…"
                                          : s.sectionTitle
                                        : s.sectionId
                                        ? `#${String(s.sectionId).slice(-6)}`
                                        : "—";

                                      return (
                                        <div
                                          key={s.sectionId}
                                          style={{
                                            padding: 10,
                                            borderRadius: 14,
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            background: "rgba(255,255,255,0.03)",
                                          }}
                                        >
                                          <div className="dash-course-meta">
                                            <span className="dash-course-meta-strong">Section</span>{" "}
                                            <span style={{ opacity: 0.85 }}>{displayTitle}</span>
                                            <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.85 }}>
                                              {label}
                                            </span>
                                          </div>

                                          <div className="dash-course-meta">
                                            Attempts:{" "}
                                            <span className="dash-course-meta-strong">{s.attempts.length}</span> • Failed:{" "}
                                            <span className="dash-course-meta-strong">{s.failures}</span>
                                          </div>

                                          {lastA?.submittedAt && (
                                            <div className="dash-course-meta">
                                              Last:{" "}
                                              <span className="dash-course-meta-strong">
                                                {formatDateTimeLocal(lastA.submittedAt)} • {score} •{" "}
                                                {lastA.isPerfect ? "✅ Perfect" : "❌ Failed"}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              </details>
                            </div>
                          );
                        })()}

                        {/* ✅ Model + Bloodborne submissions */}
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                          {/* Model */}
                          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
                            <div className="dash-course-meta" style={{ marginBottom: 4 }}>
                              <span className="dash-course-meta-strong">Model</span>{" "}
                              {model.required ? (
                                <span className={statusClassFor(model.status)} style={{ marginLeft: 8 }}>
                                  {safeStatusLabel(model.status)}
                                </span>
                              ) : (
                                <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.7 }}>
                                  Not required
                                </span>
                              )}
                            </div>

                            {model.required ? (
                              <>
                                <div className="dash-course-meta">
                                  Status: <span className="dash-course-meta-strong">{safeStatusLabel(model.status)}</span>
                                </div>

                                {(model.date || model.timeSlot) && (
                                  <div className="dash-course-meta">
                                    Scheduled:{" "}
                                    <span className="dash-course-meta-strong">
                                      {model.date ? formatDateUTC(model.date) : "—"}{" "}
                                      {model.timeSlot ? `• ${model.timeSlot}` : ""}
                                    </span>
                                  </div>
                                )}

                                {model.submittedAt ? (
                                  <>
                                    <div className="dash-course-meta">
                                      Submitted:{" "}
                                      <span className="dash-course-meta-strong">{formatDateLocal(model.submittedAt)}</span>
                                    </div>

                                    {(model.name || model.phone || model.email ) && (
                                      <div className="dash-course-meta">
                                        {model.name ? (
                                          <div>
                                            <span className="dash-course-meta-strong">{model.name}</span>
                                          </div>
                                        ) : null}
                                        {model.phone ? <div>{model.phone}</div> : null}
                                        {model.email ? <div>{model.email}</div> : null}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="dash-course-note">No model submission yet.</div>
                                )}
                              </>
                            ) : (
                              <div className="dash-course-note">This course does not require a model submission.</div>
                            )}
                          </div>

                          {/* Bloodborne */}
                          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
                            <div className="dash-course-meta" style={{ marginBottom: 4 }}>
                        <span className="dash-course-meta-strong">Bloodborne Certification</span>{" "}
                        {bb.required ? (
                          <span className={statusClassFor(bb.status)} style={{ marginLeft: 8 }}>
                            {safeStatusLabel(bb.status)}
                          </span>
                        ) : (
                          <span className="dash-status-pill" style={{ marginLeft: 8, opacity: 0.7 }}>
                            Not required
                          </span>
                        )}
                      </div>

                      {bb.required ? (
                        <>
                          <div className="dash-course-meta">
                            Status: <span className="dash-course-meta-strong">{safeStatusLabel(bb.status)}</span>
                          </div>

                          {bb.submittedAt ? (
                            <div className="dash-course-meta">
                              Submitted:{" "}
                              <span className="dash-course-meta-strong">{formatDateLocal(bb.submittedAt)}</span>
                            </div>
                          ) : null}

                          {bb.url ? (
                            <a
                              href={bb.url}
                              target="_blank"
                              rel="noreferrer"
                              className="dash-course-cta dash-course-cta-secondary"
                              style={{ marginTop: 8, display: "inline-flex", width: "fit-content" }}
                            >
                              View uploaded certificate →
                            </a>
                          ) : (
                            <div className="dash-course-note">No certificate uploaded yet.</div>
                          )}
                        </>
                      ) : (
                        <div className="dash-course-note">Bloodborne certification is not required for this course.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    )}
  </div>
)}


{isSelectedStudent && !isPending && isStaffViewer && (
  <PostCourseStaffReviewPanel
    currentUser={currentUser}
    selectedPerson={selectedPerson}
    enrollmentsForPerson={enrollmentsForPerson}
    resolveCourseFromKey={resolveCourseFromKey}
    POST_COURSE_MODULES_BY_KEY={POST_COURSE_MODULES_BY_KEY}
    fetchEnrollmentsForUser={fetchEnrollmentsForUser}
    setPersonEnrollmentsById={setPersonEnrollmentsById}
  />
)}



      {/* Apprenticeship Review (Staff only) */}
    {isSelectedStudent && !isPending && isStaffViewer && (
  <div className="dash-person-section dash-person-section--apprenticeship">
    <div
      className="dash-person-section-header dash-section-toggle"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        setApprenticeshipOpen((v) => !v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          setApprenticeshipOpen((v) => !v);
        }
      }}
      aria-expanded={apprenticeshipOpen}
      style={{ cursor: "pointer", userSelect: "none" }}
    >
      <span className="dash-section-toggle-left">
        <span className="dash-section-caret">{apprenticeshipOpen ? "▾" : "▸"}</span>
        <span>Apprenticeship Submissions</span>
      </span>
      <span className="dash-person-section-sub">
        {apprenticeship?.unlocked ? "Apprenticeship mode unlocked" : "Apprenticeship mode locked"}
      </span>
    </div>


          {certificationEarned && (
  <div style={{ marginTop: 10 }}>
    <span className="dash-apprenticeship-earned-badge">🏅 100-hr Certification Earned</span>
  </div>
)}

{apprenticeshipOpen && (
      <>
          {apprenticeshipLoading ? (
            <p className="dash-empty-text">Loading apprenticeship…</p>
          ) : apprenticeshipErr ? (
            <p className="dash-empty-text">{apprenticeshipErr}</p>
          ) : !apprenticeship ? (
            <p className="dash-empty-text">No apprenticeship record found yet.</p>
          ) : !apprenticeship.unlocked ? (
            <p className="dash-empty-text">Apprenticeship mode has not been unlocked for this student.</p>
          ) : !(apprenticeship.caseStudies || []).length ? (
            <p className="dash-empty-text">No case studies created yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {(apprenticeship.caseStudies || []).map((cs, idx) => {
                const typeLabel = String(cs?.type || "").toLowerCase() === "lip" ? "Lip" : "Brow";

                const renderSession = (sessionKey, label) => {
                  const sess = cs?.[sessionKey] || {};
                  const status = String(sess?.status || "not_submitted");
                  const photos = Array.isArray(sess?.photos) ? sess.photos : [];
                  const { before, after } = groupPhotos(photos);
                  const sessionDraftKey = `${idx}:${sessionKey}`;
                  const sessionDraft = apprenticeshipSessionDrafts[sessionDraftKey] || {
                    clientName: "",
                    sessionAt: "",
                  };
                  const sessionSaving = apprenticeshipSessionSavingKey === sessionDraftKey;
                  const hasSavedInfo = Boolean(
                    String(sess?.clientName || "").trim() || sess?.sessionAt
                  );
                  const isEditing = apprenticeshipSessionEditing[sessionDraftKey] || !hasSavedInfo;

                                    const lightboxItems = [
                    ...before.map((p, i) => ({
                      url: p.url,
                      label: `${typeLabel} · ${label} · BEFORE ${i + 1}`,
                    })),
                    ...after.map((p, i) => ({
                      url: p.url,
                      label: `${typeLabel} · ${label} · AFTER ${i + 1}`,
                    })),
                  ];

                  const isPendingReview = String(status).toLowerCase() === "pending_review";
                  const isApproved = String(status).toLowerCase() === "approved";
                  const isChanges = String(status).toLowerCase() === "changes_requested";

                  return (
                    <div
                      key={`${idx}-${sessionKey}`}
                      style={{
                        padding: 12,
                        borderRadius: 18,
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.03)",
                      }}
                    >
                      <div className="dash-course-meta" style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                        <div>
                          <span className="dash-course-meta-strong">{label}</span>
                          <span className={apprStatusClass(status)} style={{ marginLeft: 10 }}>
                            {prettyApprStatus(status)}
                          </span>
                        </div>

                        {isPendingReview && (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              className="dash-chip-button"
                              onClick={() =>
                                reviewApprenticeship({
                                  index: idx,
                                  session: sessionKey === "firstSession" ? "first" : "second",
                                  status: "approved",
                                })
                              }
                              style={{ padding: "8px 10px", fontSize: 12 }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="dash-chip-button dash-chip-button-secondary"
                              onClick={() =>
                                reviewApprenticeship({
                                  index: idx,
                                  session: sessionKey === "firstSession" ? "first" : "second",
                                  status: "changes_requested",
                                })
                              }
                              style={{ padding: "8px 10px", fontSize: 12 }}
                            >
                              Request new photos
                            </button>
                          </div>
                        )}

                        {!isPendingReview && (isApproved || isChanges) && (
                          <div className="dash-course-meta" style={{ opacity: 0.85 }}>
                            {sess?.reviewedAt ? `Reviewed: ${formatDateLocal(sess.reviewedAt)}` : ""}
                          </div>
                        )}
                      </div>

                      <div className="dash-apprenticeship-session-info">
                        <div className="dash-course-meta">Session details</div>
                        {isEditing ? (
                          <div className="dash-apprenticeship-info-grid">
                            {(() => {
                              const pickerKey = sessionDraftKey;
                              const viewMonth =
                                sessionDatePickerMonthByKey[pickerKey] ||
                                monthStartFromValue(sessionDraft.sessionDate);
                              const monthLabel = viewMonth.toLocaleDateString(undefined, {
                                month: "long",
                                year: "numeric",
                              });
                              const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
                              const firstWeekday = firstDay.getDay();
                              const daysInMonth = new Date(
                                viewMonth.getFullYear(),
                                viewMonth.getMonth() + 1,
                                0
                              ).getDate();
                              const slots = [];
                              for (let i = 0; i < firstWeekday; i += 1) slots.push(null);
                              for (let day = 1; day <= daysInMonth; day += 1) {
                                slots.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
                              }

                              return (
                                <>
                            <input
                              type="text"
                              className="dash-apprenticeship-info-input"
                              placeholder="Client name"
                              value={sessionDraft.clientName}
                              onChange={(e) =>
                                setApprenticeshipSessionDrafts((prev) => ({
                                  ...prev,
                                  [sessionDraftKey]: {
                                    ...sessionDraft,
                                    clientName: e.target.value,
                                  },
                                }))
                              }
                            />
                            <div className="dash-apprenticeship-date-wrap">
                              <button
                                type="button"
                                className="dash-apprenticeship-info-input dash-apprenticeship-date-trigger"
                                onClick={() => {
                                  setSessionDatePickerMonthByKey((prev) => ({
                                    ...prev,
                                    [pickerKey]:
                                      prev[pickerKey] || monthStartFromValue(sessionDraft.sessionDate),
                                  }));
                                  setOpenSessionDatePickerKey((prev) =>
                                    prev === pickerKey ? "" : pickerKey
                                  );
                                  setOpenSessionTimePickerKey("");
                                }}
                              >
                                <span>{formatDateInputLabel(sessionDraft.sessionDate)}</span>
                                <span className="dash-apprenticeship-date-trigger-icon">▾</span>
                              </button>
                              {openSessionDatePickerKey === pickerKey && (
                                <div className="dash-apprenticeship-date-popover">
                                  <div className="dash-apprenticeship-date-popover-head">
                                    <button
                                      type="button"
                                      className="dash-apprenticeship-date-nav"
                                      onClick={() =>
                                        setSessionDatePickerMonthByKey((prev) => ({
                                          ...prev,
                                          [pickerKey]: new Date(
                                            viewMonth.getFullYear(),
                                            viewMonth.getMonth() - 1,
                                            1
                                          ),
                                        }))
                                      }
                                    >
                                      ‹
                                    </button>
                                    <div className="dash-apprenticeship-date-month">{monthLabel}</div>
                                    <button
                                      type="button"
                                      className="dash-apprenticeship-date-nav"
                                      onClick={() =>
                                        setSessionDatePickerMonthByKey((prev) => ({
                                          ...prev,
                                          [pickerKey]: new Date(
                                            viewMonth.getFullYear(),
                                            viewMonth.getMonth() + 1,
                                            1
                                          ),
                                        }))
                                      }
                                    >
                                      ›
                                    </button>
                                  </div>
                                  <div className="dash-apprenticeship-date-weekdays">
                                    {["S", "M", "T", "W", "T", "F", "S"].map((day, dayIdx) => (
                                      <span key={`${day}-${dayIdx}`}>{day}</span>
                                    ))}
                                  </div>
                                  <div className="dash-apprenticeship-date-grid">
                                    {slots.map((slot, slotIdx) => {
                                      if (!slot) {
                                        return (
                                          <span
                                            key={`empty-${slotIdx}`}
                                            className="dash-apprenticeship-date-empty"
                                          />
                                        );
                                      }
                                      const slotValue = `${slot.getFullYear()}-${String(
                                        slot.getMonth() + 1
                                      ).padStart(2, "0")}-${String(slot.getDate()).padStart(2, "0")}`;
                                      const isSelected = slotValue === sessionDraft.sessionDate;
                                      return (
                                        <button
                                          key={slotValue}
                                          type="button"
                                          className={[
                                            "dash-apprenticeship-date-day",
                                            isSelected ? "dash-apprenticeship-date-day-selected" : "",
                                          ]
                                            .filter(Boolean)
                                            .join(" ")}
                                          onClick={() => {
                                            setApprenticeshipSessionDrafts((prev) => ({
                                              ...prev,
                                              [sessionDraftKey]: {
                                                ...sessionDraft,
                                                sessionDate: slotValue,
                                              },
                                            }));
                                            setOpenSessionDatePickerKey("");
                                            setSessionDatePickerMonthByKey((prev) => ({
                                              ...prev,
                                              [pickerKey]: new Date(
                                                slot.getFullYear(),
                                                slot.getMonth(),
                                                1
                                              ),
                                            }));
                                          }}
                                        >
                                          {slot.getDate()}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="dash-apprenticeship-select-wrap">
                              <button
                                type="button"
                                className="dash-apprenticeship-info-input dash-apprenticeship-date-trigger"
                                onClick={() => {
                                  setOpenSessionTimePickerKey((prev) =>
                                    prev === pickerKey ? "" : pickerKey
                                  );
                                  setOpenSessionDatePickerKey("");
                                }}
                              >
                                <span>
                                  {formatTimeInputLabel(sessionDraft.sessionTime, sessionTimeOptions)}
                                </span>
                                <span className="dash-apprenticeship-date-trigger-icon">▾</span>
                              </button>
                              {openSessionTimePickerKey === pickerKey && (
                                <div className="dash-apprenticeship-time-popover">
                                  {sessionTimeOptions.map((opt) => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      className={[
                                        "dash-apprenticeship-time-option",
                                        sessionDraft.sessionTime === opt.value ? "is-selected" : "",
                                      ].join(" ")}
                                      onClick={() => {
                                        setApprenticeshipSessionDrafts((prev) => ({
                                          ...prev,
                                          [sessionDraftKey]: {
                                            ...sessionDraft,
                                            sessionTime: opt.value,
                                          },
                                        }));
                                        setOpenSessionTimePickerKey("");
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="dash-chip-button"
                              disabled={sessionSaving}
                              onClick={async () => {
                                await handleUpdateApprenticeshipSessionInfo({
                                  index: idx,
                                  sessionKey,
                                  clientName: sessionDraft.clientName,
                                  sessionAt: buildSessionAtValue(sessionDraft),
                                });
                                setApprenticeshipSessionEditing((prev) => ({
                                  ...prev,
                                  [sessionDraftKey]: false,
                                }));
                              }}
                            >
                              {sessionSaving ? "Saving…" : "Save session info"}
                            </button>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="dash-apprenticeship-info-text">
                            <div>
                              <span className="dash-course-meta-strong">Client:</span>{" "}
                              {sess?.clientName || "—"}
                            </div>
                            <div>
                              <span className="dash-course-meta-strong">Date/Time:</span>{" "}
                              {(() => {
                                if (!sess?.sessionAt) return "—";
                                const d = new Date(sess.sessionAt);
                                if (Number.isNaN(d.getTime())) return "—";
                                const datePart = d.toLocaleDateString(undefined, {
                                  month: "long",
                                  day: "numeric",
                                  year: "numeric",
                                });
                                const timePart = d.toLocaleTimeString(undefined, {
                                  hour: "numeric",
                                  minute: "2-digit",
                                });
                                return `${datePart} at ${timePart}`;
                              })()}
                            </div>
                            <button
                              type="button"
                              className="dash-chip-button dash-chip-button-secondary"
                              onClick={() =>
                                setApprenticeshipSessionEditing((prev) => ({
                                  ...prev,
                                  [sessionDraftKey]: true,
                                }))
                              }
                            >
                              Update session info
                            </button>
                          </div>
                        )}
                      </div>

                      {isChanges && (
                        <div className="dash-course-note" style={{ marginTop: 8 }}>
                          Student's photos have not been approved. We've asked them to submit new photos for approval.
                        </div>
                      )}

                      {!photos.length ? (
                        <div className="dash-course-note" style={{ marginTop: 10 }}>
                          No photos submitted yet.
                        </div>
                      ) : (
                        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div>
                            <div className="dash-course-meta" style={{ marginBottom: 6 }}>
                              <span className="dash-course-meta-strong">BEFORE</span>{" "}
                              <span style={{ opacity: 0.7 }}>({before.length})</span>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
{before.map((p, i) => (
  <button
    key={`b-${i}`}
    type="button"
    className="dash-apprenticeship-photo"
    title={`Open BEFORE ${i + 1}`}
    onClick={() => openApprLightbox(lightboxItems, i, `Case Study ${idx + 1} · ${typeLabel} · ${label}`)}
    style={{ display: "block" }}
  >
    <img
      src={p.url}
      alt="before"
      style={{
        width: "100%",
        height: 78,
        objectFit: "cover",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.10)",
      }}
    />
  </button>
))}

                            </div>
                          </div>

                          <div>
                            <div className="dash-course-meta" style={{ marginBottom: 6 }}>
                              <span className="dash-course-meta-strong">AFTER</span>{" "}
                              <span style={{ opacity: 0.7 }}>({after.length})</span>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                             {after.map((p, i) => (
  <button
    key={`a-${i}`}
    type="button"
    className="dash-apprenticeship-photo"
    title={`Open AFTER ${i + 1}`}
    onClick={() =>
      openApprLightbox(
        lightboxItems,
        before.length + i,
        `Case Study ${idx + 1} · ${typeLabel} · ${label}`
      )
    }
    style={{ display: "block" }}
  >
    <img
      src={p.url}
      alt="after"
      style={{
        width: "100%",
        height: 78,
        objectFit: "cover",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.10)",
      }}
    />
  </button>
))}

                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div key={`cs-${idx}`} className="dash-course-card" style={{ padding: 16 }}>
                    {/* ✅ header row with delete */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div className="dash-course-title" style={{ minWidth: 0 }}>
                        Case Study {idx + 1} <span style={{ opacity: 0.7 }}>• {typeLabel}</span>
                      </div>

                      <button
                        type="button"
                        className="dash-chip-button dash-chip-button-secondary"
                        onClick={() => staffDeleteCaseStudy(idx)}
                        disabled={apprenticeshipSavingKey === `staff-delete:${idx}`}
                        style={{
                          padding: "8px 10px",
                          fontSize: 12,
                          opacity: apprenticeshipSavingKey === `staff-delete:${idx}` ? 0.65 : 1,
                          whiteSpace: "nowrap",
                        }}
                        title="Delete this case study so the student can start over"
                      >
                        {apprenticeshipSavingKey === `staff-delete:${idx}` ? "Deleting…" : "Delete case"}
                      </button>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
                      {renderSession("firstSession", "First Session")}
                      {renderSession("secondSession", "Second Session")}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
           </>
    )}
  </div>
)}
{effectiveIsEnrollModalOpen && (
        <EnrollStudentModal
          isOpen={effectiveIsEnrollModalOpen}
          onClose={closeEnrollModal}
          student={selectedPerson}
          courseKeys={effectiveModalCourseKey ? [effectiveModalCourseKey] : []}
    courseMeta={
      effectiveModalCourseKey
        ? [resolveCourseFromKey(effectiveModalCourseKey)].filter(Boolean)
        : []
    }
          scheduleByCourseKey={personScheduleMap}
          modelTimeSlotsByCourseKey={MODEL_TIME_SLOTS_BY_COURSE_KEY}
          onChange={handleModalChange}
          onSave={onSaveScheduleForModal}
        />
      )}
{effectiveShowCourseConfirm &&
  createPortal(
    <div className="dash-confirm-overlay" onClick={cancelCourseConfirm}>
      <div className="dash-confirm-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="dash-confirm-title">Confirm</h3>

        <p className="dash-confirm-text" style={{ whiteSpace: "pre-line" }}>
          {courseConfirmText}
        </p>

        <div className="dash-confirm-actions">
          <button
            type="button"
            className="dash-chip-button"
            onClick={runCourseConfirm}
            disabled={isCourseSaving}
          >
            {isCourseSaving ? "Saving..." : "Yes, apply"}
          </button>

          <button
            type="button"
            className="dash-chip-button dash-chip-button-secondary"
            onClick={cancelCourseConfirm}
            disabled={isCourseSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

        {/* ✅ Apprenticeship Lightbox (INK) */}
      {apprLightbox.isOpen &&
  createPortal(
    <div
      className="ink-lightbox-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeApprLightbox();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="ink-lightbox-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ink-lightbox-head">
          <div className="ink-lightbox-title">
            {apprLightbox.title || "Photo"}
            {apprLightbox.items.length > 1 ? (
              <span className="ink-lightbox-count">
                {apprLightbox.index + 1}/{apprLightbox.items.length}
              </span>
            ) : null}
          </div>

          <button type="button" className="ink-lightbox-close" onClick={closeApprLightbox} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ink-lightbox-body">
          <button
            type="button"
            className="ink-lightbox-nav ink-lightbox-prev"
            onClick={apprPrev}
            disabled={apprLightbox.items.length <= 1}
            aria-label="Previous"
          >
            ‹
          </button>

          <img
            className="ink-lightbox-img"
            src={apprLightbox.items[apprLightbox.index]?.url}
            alt={apprLightbox.items[apprLightbox.index]?.label || "Photo"}
          />

          <button
            type="button"
            className="ink-lightbox-nav ink-lightbox-next"
            onClick={apprNext}
            disabled={apprLightbox.items.length <= 1}
            aria-label="Next"
          >
            ›
          </button>
        </div>

        {apprLightbox.items[apprLightbox.index]?.label ? (
          <div className="ink-lightbox-caption">{apprLightbox.items[apprLightbox.index]?.label}</div>
        ) : null}
      </div>
    </div>,
    document.body
  )}


    </div>
  );
};

export default SelectedPersonPanel;
